import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import {
    Activity, ArrowLeft, ChevronDown, ChevronRight, Eye, Globe, LogIn, MousePointerClick, Radio, Search, Server, Users, BarChart3, List,
} from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import { useDebouncedEffect } from "../../../hooks/useDebouncedEffect";
import {
    ActivityCategory, ActivityEntry, ActivityRole, ActivityStats, ActivityTarget, ActivityUser,
    getActivityFeed, getActivityStats, getActivityUsers,
} from "../../../interfaces/activity";

// ─── Constants & helpers ──────────────────────────────────────────────────────

const LIVE_INTERVAL_MS = 5000;
// Entries are written in batches a few seconds after the request, so a live poll
// looks back a little further than the newest row it already has.
const LIVE_LOOKBACK_MS = 15000;
const SESSION_GAP_MS = 30 * 60 * 1000;

const CATEGORY_META: Record<ActivityCategory, { label: string; className: string; icon: React.ElementType }> = {
    auth: { label: "Connexion", className: "bg-violet-50 text-violet-700 border-violet-200", icon: LogIn },
    page_view: { label: "Page", className: "bg-sky-50 text-sky-700 border-sky-200", icon: Eye },
    action: { label: "Action", className: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: MousePointerClick },
    read: { label: "Lecture", className: "bg-amber-50 text-amber-700 border-amber-200", icon: List },
    api: { label: "API", className: "bg-gray-50 text-gray-500 border-gray-200", icon: Server },
};
const CATEGORY_ORDER: ActivityCategory[] = ["page_view", "action", "read", "auth", "api"];

const PAGE_NAMES: Record<string, string> = {
    "/dashboard": "Accueil",
    "/signals": "Signaux",
    "/database": "Base de données",
    "/prospection": "Listes de prospection",
    "/prospection/discover": "Découverte",
    "/prospection/:id": "Liste de prospection",
    "/client-types": "Types de clients",
    "/franchiseurs": "Franchiseurs",
    "/franchiseurs/:id": "Fiche franchiseur",
    "/multi-site": "Multi-sites",
    "/multi-site/:id": "Fiche multi-site",
    "/nurturings": "Nurturings",
    "/nurturings/create": "Nouveau nurturing",
    "/nurturings/:id": "Fiche nurturing",
    "/nurturings/:id/edit": "Édition nurturing",
    "/use-cases": "Cas d'usage",
    "/kanban": "LightCRM",
    "/restaurants/:id": "Fiche restaurant",
    "/notifications": "Notifications",
    "/settings": "Paramètres",
    "/pricing": "Tarifs",
    "/activity": "Activité",
    "/network": "Réseau",
    "/community": "Communauté",
    "/share": "Partage",
    "/email-templates": "Modèles d'email",
    "/compose-email/:id": "Rédaction email",
    "/projects/:id": "Projet",
    "/trackers/:id": "Tracker",
    "/success": "Paiement réussi",
    "/confirm-purchase": "Confirmation d'achat",
};

const TARGET_LINKS: Record<string, (id: string) => string> = {
    prospectionList: (id) => `/prospection/${id}`,
    establishment: (id) => `/restaurants/${id}`,
    franchise: (id) => `/franchiseurs/${id}`,
};

const TARGET_LABELS: Record<string, string> = {
    prospectionList: "Liste",
    establishment: "Établissement",
    signal: "Signal",
    franchise: "Franchise",
    prospect: "Prospect",
    person: "Personne",
};

function normalizePage(page: string): string {
    return (page || "")
        .split("?")[0]
        .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, "/:id")
        .replace(/\/\d+(?=\/|$)/g, "/:id");
}

function pageName(page?: string): string {
    if (!page) return "";
    const key = normalizePage(page);
    return PAGE_NAMES[key] || key;
}

function formatRelative(dateString: string) {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return "À l'instant";
    if (mins < 60) return `Il y a ${mins}min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function formatTime(dateString: string) {
    return new Date(dateString).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatDateTime(dateString: string) {
    return new Date(dateString).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function formatDay(dateString: string) {
    return new Date(dateString).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

function formatDuration(ms: number) {
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min ${s % 60 ? `${s % 60}s` : ""}`.trim();
    return `${Math.floor(m / 60)}h ${m % 60}min`;
}

function describeUserAgent(ua?: string): string {
    if (!ua) return "";
    const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
    const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "";
    return [browser, os].filter(Boolean).join(" · ") || ua.slice(0, 40);
}

function mergeEntries(current: ActivityEntry[], incoming: ActivityEntry[]): ActivityEntry[] {
    const seen = new Set(current.map((e) => e._id));
    const fresh = incoming.filter((e) => !seen.has(e._id));
    if (!fresh.length) return current;
    return [...fresh, ...current].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

// ─── Small pieces ─────────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: ActivityCategory }) {
    const meta = CATEGORY_META[category];
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border rounded text-[11px] font-medium whitespace-nowrap ${meta.className}`}>
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
}

function RoleBadges({ isAdmin, impersonated, apiKeyName }: { isAdmin?: boolean; impersonated?: boolean; apiKeyName?: string }) {
    return (
        <>
            {isAdmin && <span className="text-[10px] px-1 py-px rounded bg-gray-900 text-white font-medium shrink-0">Admin</span>}
            {impersonated && (
                <span className="text-[10px] px-1 py-px rounded bg-orange-50 border border-orange-200 text-orange-700 font-medium shrink-0" title="Action faite par un admin connecté en tant que cet utilisateur">
                    Log in as
                </span>
            )}
            {apiKeyName && (
                <span className="text-[10px] px-1 py-px rounded bg-violet-50 border border-violet-200 text-violet-700 font-medium shrink-0" title={`Appel fait avec la clé API « ${apiKeyName} »`}>
                    Clé API
                </span>
            )}
        </>
    );
}

const ROLE_OPTIONS: { value: ActivityRole; label: string }[] = [
    { value: "all", label: "Tous" },
    { value: "client", label: "Clients" },
    { value: "admin", label: "Admins" },
];

function TargetChips({ targets }: { targets?: ActivityTarget[] }) {
    if (!targets?.length) return null;
    return (
        <span className="inline-flex flex-wrap gap-1">
            {targets.map((t, i) => {
                const text = t.name || t.id?.slice(-6) || "?";
                const link = t.id && TARGET_LINKS[t.type]?.(t.id);
                const cls = "inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[11px] text-gray-700 max-w-[260px]";
                const inner = (
                    <>
                        <span className="text-gray-400">{TARGET_LABELS[t.type] || t.type}</span>
                        <span className="font-medium truncate">{text}</span>
                    </>
                );
                return link ? (
                    <a key={i} href={link} target="_blank" rel="noreferrer" className={`${cls} hover:border-gray-400`} onClick={(e) => e.stopPropagation()}>
                        {inner}
                    </a>
                ) : (
                    <span key={i} className={cls}>{inner}</span>
                );
            })}
        </span>
    );
}

function KpiCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 tabular-nums">{typeof value === "number" ? value.toLocaleString("fr-FR") : value}</p>
            {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
        </div>
    );
}

function Segmented<T extends string | number>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
    return (
        <div className="flex gap-1">
            {options.map((o) => (
                <button
                    key={String(o.value)}
                    onClick={() => onChange(o.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${value === o.value ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

// ─── Entry row ────────────────────────────────────────────────────────────────

function EntryDetails({ entry }: { entry: ActivityEntry }) {
    const rows: [string, React.ReactNode][] = [
        ["Date", new Date(entry.createdAt).toLocaleString("fr-FR")],
        ["Page", entry.page ? <span className="font-mono">{entry.page}</span> : null],
        ["Requête", entry.method ? <span className="font-mono">{entry.method} {entry.url}</span> : null],
        ["Route", entry.route ? <span className="font-mono">{entry.route}</span> : null],
        ["Statut", entry.statusCode ? `${entry.statusCode}${entry.durationMs !== undefined ? ` · ${entry.durationMs} ms` : ""}` : null],
        ["IP", entry.ip ? `${entry.ip}${entry.geo?.city || entry.geo?.country ? ` (${[entry.geo?.city, entry.geo?.country].filter(Boolean).join(", ")})` : ""}` : null],
        ["Appareil", entry.userAgent ? <span title={entry.userAgent}>{describeUserAgent(entry.userAgent)}</span> : null],
        ["Action", <span className="font-mono">{entry.action}</span>],
    ];
    return (
        <div className="px-4 pb-4 pt-1 bg-gray-50/60 border-t border-gray-100 grid md:grid-cols-2 gap-4">
            <dl className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-1 text-xs">
                {rows.filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                    <div key={k} className="contents">
                        <dt className="text-gray-400">{k}</dt>
                        <dd className="text-gray-700 break-all">{v}</dd>
                    </div>
                ))}
            </dl>
            {entry.payload ? (
                <pre className="text-[11px] leading-relaxed bg-white border border-gray-200 rounded-lg p-3 overflow-auto max-h-64 text-gray-700">
                    {JSON.stringify(entry.payload, null, 2)}
                </pre>
            ) : (
                <p className="text-xs text-gray-400 self-center">Aucun payload</p>
            )}
        </div>
    );
}

function EntryRow({ entry, showUser, onSelectUser, highlight, timeOnPage }: {
    entry: ActivityEntry;
    showUser: boolean;
    onSelectUser?: (userId: string) => void;
    highlight?: boolean;
    timeOnPage?: number;
}) {
    const [open, setOpen] = useState(false);
    const failed = (entry.statusCode ?? 0) >= 400;
    return (
        <div className={`border-b border-gray-100 last:border-0 transition-colors ${highlight ? "bg-emerald-50/70" : ""}`}>
            <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50">
                {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                <span className="text-xs text-gray-400 tabular-nums w-[64px] shrink-0" title={new Date(entry.createdAt).toLocaleString("fr-FR")}>
                    {formatTime(entry.createdAt)}
                </span>
                {showUser ? (
                    <span className="w-[230px] shrink-0 flex items-center gap-1.5 min-w-0">
                        <span
                            role="link"
                            onClick={(e) => { e.stopPropagation(); onSelectUser?.(entry.userId); }}
                            className="text-xs font-medium text-gray-700 hover:text-black hover:underline truncate"
                            title={entry.userEmail}
                        >
                            {entry.userEmail}
                        </span>
                        <RoleBadges isAdmin={entry.isAdmin} impersonated={entry.impersonated} apiKeyName={entry.apiKeyName} />
                    </span>
                ) : (
                    (entry.impersonated || entry.apiKeyName) && <RoleBadges impersonated={entry.impersonated} apiKeyName={entry.apiKeyName} />
                )}
                <span className="w-[86px] shrink-0"><CategoryBadge category={entry.category} /></span>
                <span className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${failed ? "text-red-600" : "text-gray-900"}`}>
                        {entry.category === "page_view" ? pageName(entry.page) : entry.label}
                    </span>
                    {entry.category === "page_view" && <span className="text-[11px] font-mono text-gray-400 truncate">{entry.page}</span>}
                    <TargetChips targets={entry.targets} />
                    {failed && <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-600">{entry.statusCode}</span>}
                </span>
                {timeOnPage !== undefined && (
                    <span className="text-[11px] text-gray-400 shrink-0" title="Temps avant la page suivante">{formatDuration(timeOnPage)}</span>
                )}
                {entry.category === "auth" && entry.ip && (
                    <span className="text-[11px] text-gray-500 shrink-0 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {entry.ip}{entry.geo?.city ? ` · ${entry.geo.city}` : ""}
                    </span>
                )}
                {entry.category !== "page_view" && entry.category !== "auth" && entry.page && (
                    <span className="text-[11px] text-gray-400 shrink-0 hidden lg:inline">sur {pageName(entry.page)}</span>
                )}
            </button>
            {open && <EntryDetails entry={entry} />}
        </div>
    );
}

// ─── Feed (global or per user) ────────────────────────────────────────────────

interface FeedProps {
    users: ActivityUser[];
    role?: ActivityRole;
    fixedUserId?: string;
    initialAction?: string;
    onSelectUser?: (userId: string) => void;
    grouped?: boolean;
}

function Feed({ users, role = "all", fixedUserId, initialAction, onSelectUser, grouped }: FeedProps) {
    const [entries, setEntries] = useState<ActivityEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [userId, setUserId] = useState<string>("");
    const [categories, setCategories] = useState<ActivityCategory[]>([]);
    const [action, setAction] = useState<string>(initialAction || "");
    const [showApi, setShowApi] = useState(false);
    const [q, setQ] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [live, setLive] = useState(true);
    const [highlighted, setHighlighted] = useState<Set<string>>(new Set());

    useEffect(() => { setAction(initialAction || ""); }, [initialAction]);

    const effectiveUserId = fixedUserId || userId;
    const filters = useMemo(() => ({
        role: fixedUserId ? undefined : role,
        userId: effectiveUserId || undefined,
        category: categories.length ? categories : undefined,
        hideApi: !categories.length && !showApi,
        action: action || undefined,
        q: q.trim() || undefined,
        from: from ? new Date(from + "T00:00:00").toISOString() : undefined,
        to: to ? new Date(to + "T23:59:59.999").toISOString() : undefined,
    }), [role, fixedUserId, effectiveUserId, categories, showApi, action, q, from, to]);

    useDebouncedEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getActivityFeed({ ...filters, limit: 100 })
            .then((data) => {
                if (cancelled) return;
                setEntries(data.entries);
                setHasMore(data.hasMore);
            })
            .catch((e) => !cancelled && setError(e?.response?.data?.error || "Impossible de charger l'activité"))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [filters]);

    const loadMore = async () => {
        const oldest = entries[entries.length - 1];
        if (!oldest) return;
        setLoadingMore(true);
        try {
            const data = await getActivityFeed({ ...filters, before: oldest.createdAt, limit: 100 });
            setEntries((cur) => [...cur, ...data.entries.filter((e) => !cur.some((c) => c._id === e._id))]);
            setHasMore(data.hasMore);
        } finally {
            setLoadingMore(false);
        }
    };

    // Live refresh: only while the tab is visible and no explicit end date is set
    const entriesRef = useRef(entries);
    entriesRef.current = entries;
    useEffect(() => {
        if (!live || filters.to || loading) return;
        const tick = async () => {
            if (document.hidden) return;
            const newest = entriesRef.current[0];
            const after = newest
                ? new Date(new Date(newest.createdAt).getTime() - LIVE_LOOKBACK_MS).toISOString()
                : new Date(Date.now() - LIVE_LOOKBACK_MS).toISOString();
            try {
                const data = await getActivityFeed({ ...filters, after, limit: 200 });
                const known = new Set(entriesRef.current.map((e) => e._id));
                const fresh = data.entries.filter((e) => !known.has(e._id));
                if (!fresh.length) return;
                setEntries((cur) => mergeEntries(cur, fresh));
                const ids = new Set(fresh.map((e) => e._id));
                setHighlighted(ids);
                setTimeout(() => setHighlighted((h) => (h === ids ? new Set() : h)), 3000);
            } catch {
                /* next tick retries */
            }
        };
        const id = setInterval(tick, LIVE_INTERVAL_MS);
        return () => clearInterval(id);
    }, [live, filters, loading]);

    const toggleCategory = (c: ActivityCategory) =>
        setCategories((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

    // Time on page: gap until the next page view in the same session (entries are newest first)
    const timeOnPage = useMemo(() => {
        const map = new Map<string, number>();
        let newerPageAt: number | null = null;
        let newerEventAt: number | null = null;
        for (const e of entries) {
            const t = new Date(e.createdAt).getTime();
            if (newerEventAt !== null && newerEventAt - t > SESSION_GAP_MS) newerPageAt = null;
            if (e.category === "page_view") {
                if (newerPageAt !== null) map.set(e._id, newerPageAt - t);
                newerPageAt = t;
            }
            newerEventAt = t;
        }
        return map;
    }, [entries]);

    const renderRows = (rows: ActivityEntry[]) =>
        rows.map((e) => (
            <EntryRow
                key={e._id}
                entry={e}
                showUser={!fixedUserId}
                onSelectUser={onSelectUser}
                highlight={highlighted.has(e._id)}
                timeOnPage={timeOnPage.get(e._id)}
            />
        ));

    // Per-user view: group by day, then split days into sessions (30 min of inactivity)
    const groupedView = () => {
        const days: { day: string; sessions: ActivityEntry[][] }[] = [];
        for (const e of entries) {
            const day = new Date(e.createdAt).toDateString();
            let bucket = days[days.length - 1];
            if (!bucket || bucket.day !== day) {
                bucket = { day, sessions: [[]] };
                days.push(bucket);
            }
            const session = bucket.sessions[bucket.sessions.length - 1];
            const prev = session[session.length - 1];
            if (prev && new Date(prev.createdAt).getTime() - new Date(e.createdAt).getTime() > SESSION_GAP_MS) {
                bucket.sessions.push([e]);
            } else {
                session.push(e);
            }
        }
        return days.map((d) => (
            <div key={d.day} className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1 first-letter:uppercase">{formatDay(d.sessions[0][0].createdAt)}</p>
                {d.sessions.map((s, i) => {
                    const end = new Date(s[0].createdAt).getTime();
                    const start = new Date(s[s.length - 1].createdAt).getTime();
                    const login = s.find((e) => e.category === "auth" && e.ip) || s.find((e) => e.ip);
                    return (
                        <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-2">
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-3 text-xs text-gray-500">
                                <span className="font-medium text-gray-700">
                                    Session {formatTime(s[s.length - 1].createdAt).slice(0, 5)} → {formatTime(s[0].createdAt).slice(0, 5)}
                                </span>
                                <span>{formatDuration(end - start)}</span>
                                <span>· {s.length} événement{s.length > 1 ? "s" : ""}</span>
                                <span>· {s.filter((e) => e.category === "page_view").length} pages</span>
                                {login?.ip && <span className="ml-auto flex items-center gap-1"><Globe className="w-3 h-3" />{login.ip} · {describeUserAgent(login.userAgent)}</span>}
                            </div>
                            {renderRows(s)}
                        </div>
                    );
                })}
            </div>
        ));
    };

    return (
        <div>
            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    {!fixedUserId && (
                        <select
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white max-w-[260px]"
                        >
                            <option value="">Tous les utilisateurs</option>
                            {users.filter((u) => role === "all" || (role === "admin") === u.isAdmin).map((u) => (
                                <option key={u.userId} value={u.userId}>{u.name && u.name !== u.email ? `${u.name} — ${u.email}` : u.email}</option>
                            ))}
                        </select>
                    )}
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher (action, page, email, IP, nom…)"
                            className="text-xs border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 w-[300px]"
                        />
                    </div>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" title="Du" />
                    <span className="text-xs text-gray-400">→</span>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5" title="Au" />
                    <button
                        onClick={() => setLive((l) => !l)}
                        className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            live && !to ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-gray-200 text-gray-500"
                        }`}
                        title={to ? "Désactivé quand une date de fin est définie" : ""}
                    >
                        <Radio className={`w-3.5 h-3.5 ${live && !to ? "animate-pulse" : ""}`} />
                        {live && !to ? "Live" : "Live en pause"}
                    </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {CATEGORY_ORDER.filter((c) => c !== "api").map((c) => {
                        const active = categories.includes(c);
                        const meta = CATEGORY_META[c];
                        return (
                            <button
                                key={c}
                                onClick={() => toggleCategory(c)}
                                className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${active ? meta.className : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                            >
                                {meta.label}
                            </button>
                        );
                    })}
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 ml-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showApi || categories.includes("api")}
                            onChange={(e) => {
                                setShowApi(e.target.checked);
                                if (!e.target.checked) setCategories((cur) => cur.filter((c) => c !== "api"));
                                else if (categories.length) setCategories((cur) => [...cur, "api"]);
                            }}
                        />
                        Appels API bruts
                    </label>
                    {action && (
                        <button onClick={() => setAction("")} className="ml-2 text-xs px-2 py-1 rounded-lg bg-gray-900 text-white">
                            action : {action} ✕
                        </button>
                    )}
                    <span className="ml-auto text-xs text-gray-400">{entries.length} affiché{entries.length > 1 ? "s" : ""}</span>
                </div>
            </div>

            {loading ? (
                <div className="bg-white border border-gray-200 rounded-xl flex items-center justify-center py-16 text-gray-400 text-sm">Chargement…</div>
            ) : error ? (
                <div className="bg-white border border-red-200 rounded-xl flex items-center justify-center py-16 text-red-500 text-sm">{error}</div>
            ) : entries.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl flex items-center justify-center py-16 text-gray-400 text-sm">Aucune activité</div>
            ) : grouped ? (
                groupedView()
            ) : (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">{renderRows(entries)}</div>
            )}

            {hasMore && !loading && (
                <div className="flex justify-center my-4">
                    <button onClick={loadMore} disabled={loadingMore} className="px-4 py-2 text-xs font-medium rounded-lg bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                        {loadingMore ? "Chargement…" : "Charger plus"}
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Users table ──────────────────────────────────────────────────────────────

function UsersTable({ users, loading, onSelectUser }: { users: ActivityUser[]; loading: boolean; onSelectUser: (id: string) => void }) {
    const [filter, setFilter] = useState("");
    const shown = users.filter((u) => {
        const f = filter.toLowerCase();
        return !f || u.email.toLowerCase().includes(f) || u.name.toLowerCase().includes(f) || (u.company || "").toLowerCase().includes(f);
    });
    return (
        <div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer par nom, email, société…" className="text-xs border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 w-[280px]" />
                </div>
                <span className="ml-auto text-xs text-gray-400">{shown.length} utilisateur{shown.length > 1 ? "s" : ""} actif{shown.length > 1 ? "s" : ""}</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Chargement…</div>
                ) : shown.length === 0 ? (
                    <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Aucun utilisateur actif sur la période</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                <th className="px-4 py-2.5 font-medium">Utilisateur</th>
                                <th className="px-3 py-2.5 font-medium">Statut</th>
                                <th className="px-3 py-2.5 font-medium">Dernière activité</th>
                                <th className="px-3 py-2.5 font-medium text-right">Jours actifs</th>
                                <th className="px-3 py-2.5 font-medium text-right">Pages</th>
                                <th className="px-3 py-2.5 font-medium text-right">Actions</th>
                                <th className="px-3 py-2.5 font-medium text-right">Connexions</th>
                                <th className="px-4 py-2.5 font-medium">Dernière IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((u) => {
                                const trial = !u.isSub && u.trialEndsAt;
                                const trialOver = trial && new Date(u.trialEndsAt!) < new Date();
                                return (
                                    <tr key={u.userId} onClick={() => onSelectUser(u.userId)} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer">
                                        <td className="px-4 py-2.5">
                                            <p className="font-medium text-gray-900 flex items-center gap-1.5">{u.name || u.email}<RoleBadges isAdmin={u.isAdmin} /></p>
                                            <p className="text-xs text-gray-500">{u.name && u.name !== u.email ? u.email : ""}{u.company ? ` · ${u.company}` : ""}</p>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {u.isSub ? (
                                                <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">Abonné</span>
                                            ) : trial ? (
                                                <span className={`text-[11px] px-1.5 py-0.5 rounded border ${trialOver ? "bg-red-50 border-red-200 text-red-600" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                                                    {trialOver ? "Essai expiré" : "Essai"}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-600" title={new Date(u.lastAt).toLocaleString("fr-FR")}>{formatRelative(u.lastAt)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">{u.activeDays}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">{u.pageViews}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">{u.actions}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums">{u.logins}</td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-gray-500">{u.lastIp || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// ─── Stats ────────────────────────────────────────────────────────────────────

function DailyChart({ daily, days }: { daily: ActivityStats["daily"]; days: number }) {
    // Fill missing days so the x-axis is continuous
    const byDay = new Map(daily.map((d) => [d.day, d]));
    const labels: string[] = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        labels.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    }
    const series = [
        { name: "Utilisateurs actifs", type: "column", data: labels.map((l) => byDay.get(l)?.activeUsers ?? 0) },
        { name: "Actions", type: "line", data: labels.map((l) => byDay.get(l)?.actions ?? 0) },
    ];
    const options: any = {
        chart: { toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } },
        colors: ["#1A1A1A", "#10b981"],
        stroke: { width: [0, 2], curve: "smooth" },
        plotOptions: { bar: { columnWidth: "55%", borderRadius: 3 } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: labels.map((l) => new Date(l + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })),
            tickAmount: Math.min(days, 15),
            labels: { style: { fontSize: "11px", colors: "#9ca3af" }, rotate: 0 },
        },
        // A max floor is required: with min 0 and an all-zero period the axis range
        // collapses to 0–0 and ApexCharts crashes building the SVG path.
        yaxis: [
            { seriesName: "Utilisateurs actifs", title: { text: "Utilisateurs" }, labels: { formatter: (v: number) => Math.round(v) }, min: 0, max: (m: number) => Math.max(m, 4), tickAmount: 4 },
            { seriesName: "Actions", opposite: true, title: { text: "Actions" }, labels: { formatter: (v: number) => Math.round(v) }, min: 0, max: (m: number) => Math.max(m, 4), tickAmount: 4 },
        ],
        grid: { borderColor: "#f3f4f6" },
        legend: { position: "top", horizontalAlign: "left", fontSize: "12px" },
        tooltip: { shared: true, intersect: false },
    };
    return <ReactApexChart options={options} series={series} type="line" height={280} width="100%" />;
}

function RankTable<T>({ title, rows, render, empty }: { title: string; rows: T[]; render: (row: T, max: number) => React.ReactNode; empty: string }) {
    const max = Math.max(1, ...rows.map((r: any) => r.count || 0));
    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <p className="px-4 py-3 text-sm font-semibold text-gray-900 border-b border-gray-100">{title}</p>
            {rows.length === 0 ? <p className="px-4 py-8 text-center text-xs text-gray-400">{empty}</p> : rows.map((r) => render(r, max))}
        </div>
    );
}

function Bar({ value, max }: { value: number; max: number }) {
    return (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden w-24 shrink-0">
            <div className="h-full bg-gray-800 rounded-full" style={{ width: `${(value / max) * 100}%` }} />
        </div>
    );
}

function StatsPanel({ days, role = "all", userId, onPickAction, onSelectUser }: {
    days: number;
    role?: ActivityRole;
    userId?: string;
    onPickAction?: (action: string) => void;
    onSelectUser?: (userId: string) => void;
}) {
    const [stats, setStats] = useState<ActivityStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getActivityStats(days, userId, userId ? "all" : role)
            .then((s) => !cancelled && setStats(s))
            .catch(() => !cancelled && setStats(null))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [days, userId, role]);

    if (loading) return <div className="bg-white border border-gray-200 rounded-xl flex items-center justify-center py-16 text-gray-400 text-sm">Chargement…</div>;
    if (!stats) return <div className="bg-white border border-red-200 rounded-xl flex items-center justify-center py-16 text-red-500 text-sm">Impossible de charger les statistiques</div>;

    const t = stats.totals;
    return (
        <div className="space-y-4">
            <div className={`grid grid-cols-2 ${userId ? "md:grid-cols-5" : "md:grid-cols-6"} gap-4`}>
                {!userId && <KpiCard label="Utilisateurs actifs" value={t.activeUsers} hint={`sur ${days} jours`} />}
                <KpiCard label="Pages vues" value={t.pageViews} />
                <KpiCard label="Actions" value={t.actions} />
                <KpiCard label="Connexions" value={t.logins} />
                <KpiCard label="Échecs de connexion" value={t.failedLogins} />
                <KpiCard label="Événements" value={t.events} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">Activité quotidienne</p>
                <DailyChart daily={stats.daily} days={days} />
            </div>

            <div className={`grid gap-4 ${userId ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                <RankTable
                    title="Actions les plus fréquentes"
                    rows={stats.topActions}
                    empty="Aucune action"
                    render={(a, max) => (
                        <button key={a.action} onClick={() => onPickAction?.(a.action)} className="w-full text-left px-4 py-2 flex items-center gap-3 border-b border-gray-50 last:border-0 hover:bg-gray-50">
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm text-gray-800 truncate">{a.label}</span>
                                {!userId && <span className="block text-[11px] text-gray-400">{a.users} utilisateur{a.users > 1 ? "s" : ""}</span>}
                            </span>
                            <Bar value={a.count} max={max} />
                            <span className="text-xs tabular-nums text-gray-600 w-10 text-right">{a.count}</span>
                        </button>
                    )}
                />
                <RankTable
                    title="Pages les plus visitées"
                    rows={stats.topPages}
                    empty="Aucune page vue"
                    render={(p, max) => (
                        <div key={p.page} className="px-4 py-2 flex items-center gap-3 border-b border-gray-50 last:border-0">
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm text-gray-800 truncate">{pageName(p.page)}</span>
                                <span className="block text-[11px] text-gray-400 font-mono truncate">{p.page}{!userId ? ` · ${p.users} utilisateur${p.users > 1 ? "s" : ""}` : ""}</span>
                            </span>
                            <Bar value={p.count} max={max} />
                            <span className="text-xs tabular-nums text-gray-600 w-10 text-right">{p.count}</span>
                        </div>
                    )}
                />
                {!userId && (
                    <RankTable
                        title="Utilisateurs les plus actifs"
                        rows={stats.topUsers}
                        empty="Aucun utilisateur"
                        render={(u, max) => (
                            <button key={u.userId} onClick={() => onSelectUser?.(u.userId)} className="w-full text-left px-4 py-2 flex items-center gap-3 border-b border-gray-50 last:border-0 hover:bg-gray-50">
                                <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">{u.email}</span>
                                <Bar value={u.count} max={max} />
                                <span className="text-xs tabular-nums text-gray-600 w-10 text-right">{u.count}</span>
                            </button>
                        )}
                    />
                )}
            </div>
        </div>
    );
}

// ─── User detail ──────────────────────────────────────────────────────────────

function UserDetail({ user, userId, days, users, onBack }: { user?: ActivityUser; userId: string; days: number; users: ActivityUser[]; onBack: () => void }) {
    const [view, setView] = useState<"timeline" | "stats">("timeline");
    return (
        <div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-4">
                <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black">
                    <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <div className="min-w-0">
                    <p className="text-lg font-semibold text-gray-900 truncate flex items-center gap-2">{user?.name || user?.email || "Utilisateur"}<RoleBadges isAdmin={user?.isAdmin} /></p>
                    <p className="text-xs text-gray-500">
                        {user?.email}{user?.company ? ` · ${user.company}` : ""}
                        {user?.signedUpAt ? ` · inscrit le ${new Date(user.signedUpAt).toLocaleDateString("fr-FR")}` : ""}
                    </p>
                </div>
                {user && (
                    <div className="flex gap-6 text-xs text-gray-500 ml-auto">
                        <span>Dernière activité <b className="text-gray-800">{formatDateTime(user.lastAt)}</b></span>
                        <span>Jours actifs <b className="text-gray-800">{user.activeDays}</b></span>
                        <span>Dernière IP <b className="text-gray-800 font-mono">{user.lastIp || "—"}</b></span>
                    </div>
                )}
                <div className="w-full md:w-auto">
                    <Segmented value={view} onChange={setView} options={[{ value: "timeline", label: "Chronologie" }, { value: "stats", label: "Statistiques" }]} />
                </div>
            </div>
            {view === "timeline" ? <Feed users={users} fixedUserId={userId} grouped /> : <StatsPanel days={days} userId={userId} />}
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = "feed" | "users" | "stats";

export default function UserActivity() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedUserId = searchParams.get("user") || "";
    const tab = (searchParams.get("tab") as Tab) || "feed";

    const [days, setDays] = useState(30);
    const [role, setRole] = useState<ActivityRole>("all");
    const [users, setUsers] = useState<ActivityUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    // Everyone seen over the whole retention window — for the user picker and detail header
    const [allUsers, setAllUsers] = useState<ActivityUser[]>([]);
    const [pickedAction, setPickedAction] = useState<string>("");

    useEffect(() => {
        let cancelled = false;
        setUsersLoading(true);
        getActivityUsers(days, role)
            .then((u) => !cancelled && setUsers(u))
            .catch(() => !cancelled && setUsers([]))
            .finally(() => !cancelled && setUsersLoading(false));
        return () => { cancelled = true; };
    }, [days, role]);

    useEffect(() => {
        getActivityUsers(90).then(setAllUsers).catch(() => setAllUsers([]));
    }, []);

    const setParams = useCallback((next: { tab?: Tab; user?: string }) => {
        const p = new URLSearchParams(searchParams);
        if (next.tab !== undefined) p.set("tab", next.tab);
        if (next.user !== undefined) {
            if (next.user) p.set("user", next.user);
            else p.delete("user");
        }
        setSearchParams(p);
    }, [searchParams, setSearchParams]);

    const selectUser = (id: string) => setParams({ user: id });
    const selectedUser = allUsers.find((u) => u.userId === selectedUserId);

    const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
        { id: "feed", label: "Flux", icon: Activity },
        { id: "users", label: "Utilisateurs", icon: Users },
        { id: "stats", label: "Statistiques", icon: BarChart3 },
    ];

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Activité utilisateurs</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Tout ce que font les clients et les admins dans l'app · conservé 90 jours</p>
                    </div>
                </div>

                <BONavigation />

                {selectedUserId ? (
                    <UserDetail user={selectedUser} userId={selectedUserId} days={days} users={allUsers} onBack={() => setParams({ user: "" })} />
                ) : (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <nav className="flex gap-6 border-b border-gray-200">
                                {tabs.map((t) => {
                                    const Icon = t.icon;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setParams({ tab: t.id })}
                                            className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center gap-2 ${tab === t.id ? "border-black text-black" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </nav>
                            <div className="flex items-center gap-3">
                                <Segmented value={role} onChange={setRole} options={ROLE_OPTIONS} />
                                {tab !== "feed" && (
                                    <Segmented value={days} onChange={setDays} options={[{ value: 7, label: "7 j" }, { value: 30, label: "30 j" }, { value: 90, label: "90 j" }]} />
                                )}
                            </div>
                        </div>

                        {tab === "feed" && <Feed users={allUsers} role={role} onSelectUser={selectUser} initialAction={pickedAction} />}
                        {tab === "users" && <UsersTable users={users} loading={usersLoading} onSelectUser={selectUser} />}
                        {tab === "stats" && (
                            <StatsPanel
                                days={days}
                                role={role}
                                onSelectUser={selectUser}
                                onPickAction={(a) => { setPickedAction(a); setParams({ tab: "feed" }); }}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
